import { Specification } from "@amzn/amazonian-hardware-catalogical";
import { PreCheckoutInformation } from "common/types";
import { CatalogicalItem, CatalogicalRequestContent, ItemStatus } from "common/types/catalogical-type";
import { useGetItem, useGetItems } from "hooks/catalogical-itsoftware";
import jp from "jsonpath";
import { IOrdersService, OrdersServiceFactory } from "lib/services";
import { ItemTaxonomyId, normalizeSoftwareTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import { IItemVersion, ItemDetails, ItemSubtypes, ItemTypes } from "lib/types/items";
import _ from "lodash";
import { useEffect, useState } from "react";

import defaultVersionsData from "./defaultVersions.json";
import { useGetItemDetailsResult } from "./useGetItemDetailsFactory";

const GET_PARENT_INFO_IF_NULL = true;

export const useCatalogicalGetSoftwareDetails = (): useGetItemDetailsResult => {
  const { item: itemParent, error: errorGetItem, isLoading: isLoadingGetItem, doGetItem } = useGetItem();
  const { items: itemChildren, error: errorGetItems, isLoading: isLoadingGetItems, doGetItems } = useGetItems();
  const [preCheckoutData, setPreCheckoutData] = useState<PreCheckoutInformation | undefined>(undefined);
  const [mappedItem, setMappedItem] = useState<ItemDetails | undefined>(undefined);
  const [defaultVersion, setDefaultVersion] = useState<IItemVersion | undefined>(undefined);
  const [versions, setVersions] = useState<IItemVersion[]>([]);

  const [error, setError] = useState<Error | undefined>(undefined);
  useEffect(() => {
    if (itemParent && itemChildren) {
      setHookInfo();
    }
  }, [itemParent, itemChildren]);

  const setHookInfo = () => {
    setVersions([]);
    setMappedItem(undefined);
    // Item has versions
    if (itemChildren && itemChildren.length > 0) {
      // set first item as default
      let defaultVersionToSelect: IItemVersion = mapVersion(itemChildren[0])!;
      // order by short description
      const orderedVersions = _.orderBy(itemChildren, [(item) => item.shortDescription?.toLowerCase()], ["asc"]);

      // check default items json for default version
      const defaultVersionInfo = defaultVersionsData.defaultVersions.find((entry) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        const normalizedTaxonomyId = normalizeSoftwareTaxonomyId(itemParent?.taxonomyId!); //
        return entry.itemId === normalizedTaxonomyId;
      });
      // Find if there is a default version and move it to the first position
      if (orderedVersions && defaultVersionInfo) {
        const defaultVersionId = defaultVersionInfo.defaultVersion.id;
        const defaultVersionIndex = orderedVersions.findIndex(
          (version) => normalizeSoftwareTaxonomyId(version.taxonomyId) === defaultVersionId
        );

        // Choose version for dropdown
        if (orderedVersions[defaultVersionIndex]) {
          defaultVersionToSelect = mapVersion(orderedVersions[defaultVersionIndex])!;
        }

        if (defaultVersionIndex > 0) {
          const [defaultVersion] = orderedVersions.splice(defaultVersionIndex, 1);
          orderedVersions.unshift(defaultVersion);
        }
      }
      setMappedItem(mapItem(itemParent!));
      setDefaultVersion(defaultVersionToSelect);
      setVersions(mapVersions(orderedVersions));
    } else {
      setMappedItem(mapItem(itemParent!));
      setDefaultVersion(mapVersion(itemParent));
    }
  };

  const fetchItem = async (taxonomyId: ItemTaxonomyId) => {
    doGetItem(taxonomyId.toString());
    const params: CatalogicalRequestContent = {
      limit: 500,
      offset: 0,
      sortBy: "name",
      sortDirection: "asc",
      filter: JSON.stringify({ parentTaxonomyId: taxonomyId.toString() }),
    };
    doGetItems({ searchParams: params });
  };

  const fetchPreCheckoutData = async (token: string) => {
    setPreCheckoutData(undefined);
    try {
      const taxonomyIdWithoutStrain = normalizeSoftwareTaxonomyId(
        ItemTaxonomyId.Parse(itemParent!.taxonomyId).toString()
      );
      const ordersService: IOrdersService = OrdersServiceFactory.getInstance(taxonomyIdWithoutStrain);
      const checkoutData = await ordersService.GetPreCheckoutInformation(taxonomyIdWithoutStrain, token);
      if (!checkoutData.information) {
        throw new Error("Couldn't obtain pre checkout information");
      }
      setPreCheckoutData(checkoutData.information);
    } catch (error) {
      setError(error as Error);
    }
  };

  const mapVersions = (versions: CatalogicalItem[]): IItemVersion[] => {
    if (!versions || versions.length === 0) {
      return [];
    }
    return versions.map((catalogVersion) => mapVersion(catalogVersion, GET_PARENT_INFO_IF_NULL)!);
  };

  const mapVersion = (
    version: CatalogicalItem | undefined,
    checkParent: boolean = false,
    originalItems?: CatalogicalItem[]
  ): IItemVersion | undefined => {
    if (!version) {
      return undefined;
    }

    // Find the corresponding catalog item for this version to get the orderRedirectURL
    const correspondingItem = originalItems?.find((item) => item.itemId === version.id);
    return {
      taxonomyId: version.taxonomyId,
      name: version.name,
      displayText: getValue(checkParent, version.shortDescription, "version.shortDescription"),
      status: version?.status || ItemStatus.unavailable,
      amount: getValue(checkParent, version.price?.amount, "price.amount", 0),
      currency: getValue(checkParent, version.price?.currency, "price.currency"),
      logoUrl: getValue(checkParent, version.logoUrl, "logoUrl"),
      orderWorkflow: getValue(checkParent, version.orderWorkflow, "orderWorkflow"),
      applinkInstanceName: getValue(checkParent, version?.applinkInstanceName, "applinkInstanceName"),
      applinkProductName: getValue(checkParent, version?.applinkProductName, "applinkProductName"),
      isReturnable: false,
      orderRedirectUrl: correspondingItem?.references?.orderRedirectUrl,
      acme: {
        isEnabled: version?.acme?.isEnabled ?? false,
        windowsDeepLinkUrl: version?.acme?.windowsDeepLinkUrl,
        macOsDeepLinkUrl: version?.acme?.macOsDeepLinkUrl,
      },
      requiresLicenseCapValidation: getValue(
        checkParent,
        version.requiresLicenseCapValidation,
        "requiresLicenseCapValidation"
      ),
    };
  };

  const updateVersionSelected = (selectedVersion: IItemVersion) => {
    if (itemChildren && itemChildren?.length > 0) {
      const newSelectedChild = itemChildren.find((child) => child.taxonomyId === selectedVersion.taxonomyId);
      setDefaultVersion(mapVersion(newSelectedChild, GET_PARENT_INFO_IF_NULL));
      setMappedItem(mapItem(newSelectedChild!, GET_PARENT_INFO_IF_NULL));
    }
  };

  const mapItem = (item: CatalogicalItem, checkParent: boolean = false): ItemDetails => {
    return {
      taxonomyId: getValue(checkParent, item.taxonomyId, "taxonomyId"),
      name: getValue(checkParent, item.name, "name"),
      description: getValue(checkParent, item.description, "description", ""),
      shortDescription: getValue(checkParent, item.shortDescription, "shortDescription", ""),
      status: (getValue(checkParent, item.status, "status") as string).toLowerCase(),
      logoUrl: getValue(checkParent, item.logoUrl, "logoUrl", ""),
      publisher: getValue(checkParent, item.manufacturer, "manufacturer", ""),
      etaDays: getValue(checkParent, item.etaDays, "etaDays", 0),
      os: item.os || [],
      categories: item.categories || item.tags || [],
      priceAmount: getValue(checkParent, item.price?.amount, "price.amount", 1),
      priceCurrency: getValue(checkParent, item.price?.currency, "price.currency", ""),
      hasStock: getValue(checkParent, item.hasStock, "hasStock", false),
      orderWorkflow: getValue(checkParent, item.orderWorkflow, "orderWorkflow"),
      orderRedirectUrl: getValue(
        checkParent,
        item.references?.orderRedirectUrl || item.orderRedirectUrl,
        "references.orderRedirectUrl"
      ),
      externalButtonText: getValue(checkParent, item.externalButtonText, "externalButtonText"),
      moreDetailsUrl: getValue(checkParent, item.references?.moreDetailsUrl, "references.moreDetailsUrl"),
      parentName: itemParent?.name,
      parentTxId: itemParent?.taxonomyId || item.parentTaxonomyItemId,
      requiresLicenseCapValidation:
        item.specifications?.find((spec) => spec.key === "requiresLicenseCapValidation")?.value === "true",
      applinkProductName: item.applinkProductName,
      applinkInstanceName: item.applinkInstanceName,
      isReturnable: item.isReturnable,
      subtype: item.subtype as ItemSubtypes | undefined,
      acme: item.acme,
      itptClassification: item.itptClassification,
      version: item.version,
      dataClassification: item.dataClassification,
      securityStatus: item.securityStatus,
      feedback: item.feedback,
    };
  };

  const getValue = (checkParent: boolean, value: any, key: string, defaultValue?: any) => {
    const valueOrDefault = value ?? defaultValue ?? "";
    if (!checkParent) {
      return valueOrDefault;
    }

    // Should get value from parent if child value is not present
    if (itemParent && jp.value(itemParent, key)) {
      return jp.value(itemParent, key);
    }

    return valueOrDefault;
  };

  return {
    type: ItemTypes.SOFTWARE,
    item: mappedItem,
    isLoading: isLoadingGetItem || isLoadingGetItems,
    error: errorGetItem ?? errorGetItems ?? error,
    defaultVersion,
    versions,
    updateVersionSelected,
    getItem: fetchItem,
    preCheckoutData,
    fetchPreCheckoutData,
  };
};
